import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  addToast,
  Button,
  Card,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  useTabParam,
} from '@structural-climbing/ui'
import { useGymContext } from '../contexts/GymContext'
import { useGymThemeContext } from '../layouts/useGymTheme'
import { MemberIdentity } from '../features/members/MemberIdentity'
import { MemberSearchInput } from '../features/members/MemberSearchInput'
import { CopyLinkButton } from '../features/requests/CopyLinkButton'
import { memberAppUrl } from '../features/requests/memberAppUrl'
import { ApproveRequestModal } from '../features/requests/ApproveRequestModal'
import type { ApproveParams } from '../features/requests/ApproveRequestModal'
import type { DeskCandidate, DeskMatch } from '../features/requests/DeskMatchBlock'
import type { GymPlan } from '../features/plans/types'
import { PageTabs } from '../components/PageTabs'
import { PageHeader } from '../components/PageHeader'

type RequestStatus = 'pending' | 'approved' | 'rejected'

interface JoinRequest {
  id: string
  user_id: string | null
  user_email: string
  user_name: string
  user_phone: string | null
  user_date_of_birth: string | null
  status: RequestStatus
  is_guest: boolean
  notes: string | null
  created_at: string
  signature_keys: Record<string, string> | null
  /** Set on a request for someone under 18: the adult who signed for them. */
  guardian_first_name?: string | null
  guardian_last_name?: string | null
  guardian_email?: string | null
  guardian_phone?: string | null
  /** A member the gym added itself, in person. */
  added_at_desk?: boolean
  /** Pending only: someone already added at the desk, with the same verified email. */
  desk_match?: DeskMatch | null
  profiles?: {
    first_name: string | null
    last_name: string | null
    nickname: string | null
    avatar_ext: string | null
    avatar_updated_at: string | null
  } | null
}

/**
 * The name the request was submitted under is a snapshot, kept because the
 * signed waiver refers to it and because a guest has no profile at all. For
 * showing a person, prefer who they are now: the row already renders their live
 * nickname and avatar, so a frozen name beside them reads as a mismatch.
 */
function requesterName(req: JoinRequest): string {
  const live = [req.profiles?.first_name, req.profiles?.last_name].filter(Boolean).join(' ').trim()
  return live || req.user_name
}

/** Lower-cased and stripped of diacritics, so "stefan" finds "Ștefan". */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

/**
 * Every word typed has to appear somewhere on the request, in any order. The
 * snapshot name is searched alongside the live one because staff may know the
 * person by the name on the waiver.
 */
function matchesSearch(req: JoinRequest, search: string): boolean {
  const words = fold(search).split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const haystack = fold(
    [requesterName(req), req.user_name, req.profiles?.nickname, req.user_email, req.user_phone]
      .filter(Boolean)
      .join(' '),
  )
  return words.every((w) => haystack.includes(w))
}

function calcAge(dob: string): number {
  const today = new Date()
  const birth = new Date(dob)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

interface RequiredDocument {
  id: string
  title: string
  /** Who signs it: the member, or a parent for someone under 18. */
  audience?: 'member' | 'child'
}

/**
 * The documents this request had to sign. A minor's request is signed by a
 * parent on the children's document, never on the member documents, so listing
 * those would show a signed request as unsigned.
 */
/**
 * A request that came in with nothing signed: the gym has no document for this
 * person to sign online, a minor at a gym with no children's document most of
 * all. The desk has to have the agreement signed on paper, and this is what
 * tells it so. A member the gym added at the desk was dealt with in person.
 */
function nothingSigned(req: JoinRequest): boolean {
  if (req.added_at_desk) return false
  return !req.signature_keys || Object.keys(req.signature_keys).length === 0
}

function documentsFor(req: JoinRequest, docs: RequiredDocument[]): RequiredDocument[] {
  const audience = req.guardian_first_name ? 'child' : 'member'
  return docs.filter((doc) => (doc.audience ?? 'member') === audience)
}

const STATUS_TABS: RequestStatus[] = ['pending', 'approved', 'rejected']

const NO_REQUESTS_KEY: Record<RequestStatus, string> = {
  pending: 'requests.noPendingRequests',
  approved: 'requests.noApprovedRequests',
  rejected: 'requests.noRejectedRequests',
}

export function RequestsPage() {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { primaryColor } = useGymThemeContext()
  const { slug } = useGymContext()

  // In the URL, so a refresh stays on Approved or Rejected.
  const [tab, setTab] = useTabParam<RequestStatus>('status', STATUS_TABS, 'pending')
  const [requests, setRequests] = useState<JoinRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingAction, setPendingAction] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [requiredDocs, setRequiredDocs] = useState<RequiredDocument[]>([])
  const [gymPlans, setGymPlans] = useState<GymPlan[]>([])
  const [approveTarget, setApproveTarget] = useState<JoinRequest | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<JoinRequest | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Kept across tabs, so the same name can be checked under Approved or Rejected.
  const [search, setSearch] = useState('')

  const visible = useMemo(
    () => requests.filter((r) => matchesSearch(r, search)),
    [requests, search],
  )

  const TAB_LABELS: Record<RequestStatus, string> = {
    pending: t('requests.tabPending'),
    approved: t('requests.tabApproved'),
    rejected: t('requests.tabRejected'),
  }

  const loadRequests = useCallback(
    async (status: RequestStatus) => {
      setLoading(true)
      setActionError(null)
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/join-requests?status=${status}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) setRequests((await res.json()) as JoinRequest[])
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    void loadRequests(tab)
  }, [tab, loadRequests])

  useEffect(() => {
    async function loadMeta() {
      try {
        const token = await getAccessTokenSilently()
        const [docsRes, plansRes] = await Promise.all([
          fetch(`${apiUrl}/gym-required-documents?audience=all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${apiUrl}/gym-plans`, { headers: { Authorization: `Bearer ${token}` } }),
        ])
        if (docsRes.ok) setRequiredDocs((await docsRes.json()) as RequiredDocument[])
        if (plansRes.ok) {
          const all = (await plansRes.json()) as GymPlan[]
          setGymPlans(all.filter((p) => p.is_active))
        }
      } catch {
        // silently ignore
      }
    }
    void loadMeta()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleApprove(req: JoinRequest, params: ApproveParams) {
    setPendingAction(req.id)
    setActionError(null)
    try {
      const token = await getAccessTokenSilently()
      const body: Record<string, unknown> = {
        price_paid: params.pricePaid,
        payment_method: params.paymentMethod,
      }
      if (params.planId) body.plan_id = params.planId
      if (params.guardianConfirmed) body.guardian_confirmed = true
      if (params.mergeDeskUserId) body.merge_desk_user_id = params.mergeDeskUserId
      if (params.isTransfer && params.transferStartDate) body.starts_at = params.transferStartDate
      if (params.isTransfer && params.transferEntriesRemaining !== '')
        body.entries_remaining = parseInt(params.transferEntriesRemaining)
      if (params.billing) {
        body.billing = {
          billing_type: params.billing.billingType,
          first_name: params.billing.firstName || undefined,
          last_name: params.billing.lastName || undefined,
          cnp: params.billing.cnp || undefined,
          company_name: params.billing.companyName || undefined,
          cui: params.billing.cui || undefined,
          reg_com: params.billing.regCom || undefined,
          address: params.billing.address || undefined,
          city: params.billing.city || undefined,
          county: params.billing.county || undefined,
        }
      }
      const res = await fetch(`${apiUrl}/join-requests/${req.id}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = (await res.json()) as { error?: string; code?: string; merged?: unknown }
      if (!res.ok) {
        const mergeError = data.code ? `requests.mergeError${data.code}` : null
        setActionError(
          mergeError && i18n.exists(mergeError)
            ? t(mergeError)
            : (data.error ?? t('requests.errorApprove')),
        )
        return
      }
      if (data.merged) {
        addToast({
          title: t('requests.mergedToast', { name: requesterName(req) }),
          color: 'success',
        })
      }
      setApproveTarget(null)
      void loadRequests(tab)
    } finally {
      setPendingAction(null)
    }
  }

  async function getDesk<T>(requestId: string, query = ''): Promise<T | null> {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl}/join-requests/${requestId}/desk-candidates${query}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return res.ok ? ((await res.json()) as T) : null
  }

  async function handleReject(req: JoinRequest) {
    setPendingAction(req.id)
    setActionError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/join-requests/${req.id}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const data = (await res.json()) as { error?: string }
      if (!res.ok) {
        setActionError(data.error ?? t('requests.errorReject'))
        return
      }
      void loadRequests(tab)
    } finally {
      setPendingAction(null)
    }
  }

  async function handleDeleteRequest() {
    if (!deleteTarget) return
    setIsDeleting(true)
    setDeleteError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/join-requests/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = (await res.json()) as { error?: string }
      if (!res.ok) {
        setDeleteError(data.error ?? t('requests.deleteError'))
        return
      }
      setDeleteTarget(null)
      void loadRequests(tab)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('requests.title')}
        subtitle={t('requests.subtitle')}
        actions={
          <>
            <CopyLinkButton
              label={t('requests.memberLinkButton')}
              url={memberAppUrl(`/join/${slug}`)}
            />
            <CopyLinkButton
              label={t('requests.guestLinkButton')}
              url={memberAppUrl(`/guest/${slug}`)}
            />
          </>
        }
      />

      <div className="mb-4 flex flex-col gap-3">
        <PageTabs
          ariaLabel={t('requests.title')}
          selectedKey={tab}
          onSelectionChange={setTab}
          className=""
          items={STATUS_TABS.map((s) => ({ key: s, label: TAB_LABELS[s] }))}
        />
        <MemberSearchInput value={search} onValueChange={setSearch} className="w-full" />
        {actionError && <p className="text-sm text-danger">{actionError}</p>}
      </div>
      <div>
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="sm" color="primary" />
          </div>
        ) : requests.length === 0 ? (
          <p className="py-8 text-center text-sm text-foreground/40">{t(NO_REQUESTS_KEY[tab])}</p>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-foreground/40">
            {t('requests.noSearchResults')}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((req) => (
              <Card
                key={req.id}
                shadow="sm"
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <MemberIdentity
                        userId={req.user_id}
                        name={requesterName(req)}
                        nickname={req.profiles?.nickname}
                        email={req.user_email}
                        avatarExt={req.profiles?.avatar_ext}
                        avatarUpdatedAt={req.profiles?.avatar_updated_at}
                        size="md"
                        primaryColor={primaryColor}
                      />
                      {req.is_guest && (
                        <Chip size="sm" variant="flat" color="secondary" className="shrink-0">
                          {t('requests.guestBadge')}
                        </Chip>
                      )}
                      {!req.user_id && !req.is_guest && (
                        <span className="shrink-0 rounded-full bg-default/10 px-1.5 py-0.5 text-xs text-foreground/40">
                          {t('requests.noAccountBadge')}
                        </span>
                      )}
                    </div>
                    {req.user_phone && (
                      <p className="text-xs text-foreground/40">{req.user_phone}</p>
                    )}
                    {req.user_date_of_birth &&
                      (() => {
                        const age = calcAge(req.user_date_of_birth)
                        return (
                          <p className="text-xs text-foreground/40">
                            {t('requests.age', { age })}
                            {age < 18 && (
                              <span className="ml-1.5 rounded-full bg-warning/10 px-1.5 py-0.5 text-xs text-warning">
                                {t('requests.minorBadge')}
                              </span>
                            )}
                          </p>
                        )
                      })()}
                    {req.guardian_first_name && (
                      <p className="text-xs text-foreground/40">
                        {t(
                          nothingSigned(req) ? 'requests.guardianNamed' : 'requests.guardianLine',
                          {
                            name: [req.guardian_first_name, req.guardian_last_name]
                              .filter(Boolean)
                              .join(' '),
                          },
                        )}
                        {req.guardian_phone && ` · ${req.guardian_phone}`}
                      </p>
                    )}
                    <p className="mt-0.5 text-xs text-foreground/30">
                      {new Date(req.created_at).toLocaleDateString()}
                    </p>
                    {req.desk_match && (
                      <Chip size="sm" color="primary" variant="flat" className="mt-1.5">
                        {t('requests.deskMatchBadge')}
                      </Chip>
                    )}
                    {nothingSigned(req) && (
                      <p
                        role="alert"
                        className="mt-1.5 rounded-md border border-danger/40 bg-danger/10 px-2 py-1 text-xs font-semibold text-danger"
                      >
                        {t(
                          req.guardian_first_name
                            ? 'requests.nothingSignedMinor'
                            : 'requests.nothingSigned',
                        )}
                      </p>
                    )}
                    {documentsFor(req, requiredDocs).length > 0 && (
                      <div className="mt-1.5 flex flex-col gap-0.5">
                        {documentsFor(req, requiredDocs).map((doc) => {
                          const signed = !!req.signature_keys?.[doc.id]
                          return (
                            <div key={doc.id} className="flex items-center gap-1.5">
                              <span
                                className={`text-xs font-medium ${signed ? 'text-success' : 'text-danger'}`}
                              >
                                {signed ? '✓' : '✗'}
                              </span>
                              <span className="text-xs text-foreground/50">{doc.title}</span>
                              {signed && (
                                <button
                                  type="button"
                                  className="text-xs text-primary underline"
                                  onClick={async () => {
                                    const token = await getAccessTokenSilently()
                                    const res = await fetch(
                                      `${apiUrl}/join-signatures/${req.id}/${doc.id}`,
                                      { headers: { Authorization: `Bearer ${token}` } },
                                    )
                                    if (res.ok) window.open(URL.createObjectURL(await res.blob()))
                                  }}
                                >
                                  {t('requests.view')}
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {tab === 'pending' && (
                  <div className="flex shrink-0 gap-2 self-end sm:self-auto">
                    <Button
                      size="sm"
                      color="primary"
                      isDisabled={pendingAction !== null}
                      onClick={() => setApproveTarget(req)}
                    >
                      {t('requests.approve')}
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      isLoading={pendingAction === req.id}
                      isDisabled={pendingAction !== null}
                      onClick={() => void handleReject(req)}
                    >
                      {t('requests.reject')}
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="danger"
                      title={t('requests.deleteButton')}
                      isDisabled={pendingAction !== null}
                      onClick={() => setDeleteTarget(req)}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </Button>
                  </div>
                )}

                {tab === 'approved' && (
                  <span className="shrink-0 self-end rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success sm:self-auto">
                    {t('requests.approvedBadge')}
                  </span>
                )}

                {tab === 'rejected' && (
                  <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                    <span className="rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
                      {t('requests.rejectedBadge')}
                    </span>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="danger"
                      title={t('requests.deleteButton')}
                      onClick={() => setDeleteTarget(req)}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <ApproveRequestModal
        request={approveTarget}
        gymPlans={gymPlans}
        isLoading={pendingAction === approveTarget?.id}
        onApprove={(p) => void handleApprove(approveTarget!, p)}
        onClose={() => setApproveTarget(null)}
        loadDeskCandidates={(id) => getDesk<DeskCandidate[]>(id)}
        loadDeskMember={(id, userId) =>
          getDesk<DeskMatch>(id, `?user_id=${encodeURIComponent(userId)}`)
        }
      />

      <Modal
        scrollBehavior="inside"
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null)
            setDeleteError(null)
          }
        }}
      >
        <ModalContent>
          <ModalHeader>{t('requests.deleteModalTitle')}</ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground/70">
              {t('requests.deleteModalBody', {
                name: deleteTarget ? requesterName(deleteTarget) : '',
              })}
            </p>
            {deleteError && <p className="text-sm text-danger">{deleteError}</p>}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              isDisabled={isDeleting}
              onClick={() => {
                setDeleteTarget(null)
                setDeleteError(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color="danger"
              isLoading={isDeleting}
              onClick={() => void handleDeleteRequest()}
            >
              {t('requests.deleteConfirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
